import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SessionContext } from './context';
import { Profile } from '../../api/types/profile';
import { ProjectErrors, ProjectErrorType } from '../../utils/errors/types';
import { firebaseLogout, loginWithEmail, sendPasswordRecoverEmail } from '../../api/auth';
import { getProjectErrorForCode, isProjectError } from '../../utils/errors';
import { User, UserCredential } from '@firebase/auth';
import { getProfileByUserId } from '../../api/profile';
import { auth } from '../../firebase';
import { ActivityIndicatorContext } from '../ActivityIndicatorProvider/context';
import useLocalStorage from '../../hooks/useLocalStorage';
import { LOCAL_STORAGE_KEYS } from '../../defines';
import { getInitials } from '../../api/profile/utils';
import * as Sentry from '@sentry/react';
import { ToastContext } from '../ToastProvider/context';

const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast } = useContext(ToastContext);

  const [currentProfile, setCurrentProfile] = useState<Profile | undefined>();
  const [isLoggedIn, setIsLoggedIn] = useLocalStorage(LOCAL_STORAGE_KEYS.isLoggedIn, false);
  const initializingRef = useRef(true);

  const resumeSession = async (initialUserUid: string) => {
    setIsLoading(true);
    const profile = await getProfileByUserId(initialUserUid, true);
    setIsLoading(false);
    const error = isProjectError(profile);
    if (error) {
      Sentry.captureException(
        {
          ...error,
          details: 'Failed to resume session. Profile is invalid. Details: ' + error?.details,
        },
        { level: 'error' },
      );
      console.log('[AUTH]', 'Failed to resume session. Profile is invalid. Details:', error?.code);
      logout();
      return;
    }
    if (!profile) {
      showErrorToast(getProjectErrorForCode(ProjectErrors.noUserFoundWithID));
      return;
    }

    const typedProfile = profile as Profile;
    onLogin(typedProfile);
  };

  const onAuthStateChanged = async (user: User | null | undefined) => {
    if (user && initializingRef.current) {
      await resumeSession(user.uid);
    }
    if (!user) {
      onLogout();
    }
    initializingRef.current = false;
  };

  useEffect(() => {
    return auth.onAuthStateChanged(onAuthStateChanged);
  }, []);

  const refreshProfile = async () => {
    const userId = await auth?.currentUser?.uid;
    if (userId) {
      const result = await getProfileByUserId(userId, true);
      const resultError = isProjectError(result);
      if (resultError) {
        Sentry.captureException(resultError, { level: 'error' });
        logout();
      } else if (result && 'id' in result) {
        const initials = getInitials({ firstName: result?.firstName, lastName: result?.lastName });
        const fullName = result?.fullName || `${result.firstName} ${result.lastName}`;
        setCurrentProfile({ initials, fullName, ...result });
      }
    }
  };

  const login = async (email: string, password: string): Promise<ProjectErrorType | undefined> => {
    const loginResult = await loginWithEmail(email, password);
    const loginError = isProjectError(loginResult);
    if (loginError) {
      Sentry.captureException(loginError, { level: 'error' });
      logout();
      return loginError;
    }

    const { user } = loginResult as UserCredential;
    if (!user.uid) {
      logout();
      return getProjectErrorForCode(ProjectErrors.authInvalidUserId);
    }

    const profileResult = await getProfileByUserId(user?.uid, true);
    const profileError = isProjectError(profileResult);
    if (profileError) {
      Sentry.captureException(profileError, { level: 'error' });
      logout();
      return profileError;
    }

    if (!profileResult) {
      showErrorToast(getProjectErrorForCode(ProjectErrors.noUserFoundWithID));
      return;
    }

    const typedResult = profileResult as Profile;
    onLogin(typedResult);
  };

  const recoverPassword = async (email: string): Promise<void> => {
    return await sendPasswordRecoverEmail(email);
  };

  const logout = () => {
    onLogout();
    try {
      firebaseLogout().then();
    } catch (error) {}
  };

  const value = useMemo(
    () => ({
      currentProfile,
      isLoggedIn,
      refreshProfile,
      login,
      recoverPassword,
      logout,
    }),
    [currentProfile, isLoggedIn],
  );

  const onLogin = (profile: Profile, email?: string) => {
    const initials = getInitials({ firstName: profile?.firstName, lastName: profile?.lastName });
    const fullName = profile?.fullName || `${profile.firstName} ${profile.lastName}`;
    setCurrentProfile({ initials, fullName, ...profile });
    setIsLoggedIn(true);
    Sentry.setUser({
      key: profile.id,
      id: profile.id,
      email: email || '-',
    });
    console.log('[AUTH] User just logged in');
  };

  const onLogout = () => {
    setCurrentProfile(undefined);
    setIsLoggedIn(false);
    Sentry.setUser(null);
    console.log('[AUTH] User just logged out');
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export default SessionProvider;
